class CollectionProductsGrid extends HHBaseElement {
  loading = false
  requestId = 0

  constructor() {
    super()
    this.$grid = this.querySelector('.collection-grid')
    this.$backdrop = this.querySelector('.js-backdrop')
    this._handleLoadMore = this._handleLoadMore.bind(this)
    this._handleFiltersChange = this._handleFiltersChange.bind(this)
    this._handleFilterFormSubmit = this._handleFilterFormSubmit.bind(this)
    this._handleClearAllClick = this._handleClearAllClick.bind(this)
    this._handleLocationPopState = this._handleLocationPopState.bind(this)
    this._handleFiltersPointerDown = this._handleFiltersPointerDown.bind(this)
    this.$filtersForm = this.querySelector('[data-filters-form]')
    this.$filtersFormSubmitable = this.querySelector(
      '[data-filter-form-submitable]'
    )
    this.$slideoutContainer = document.querySelector(
      `[slideout-id="mobile-filters"]`
    )
  }

  connectedCallback() {
    // Infinite scroll appends pages, so a restored scroll offset points at content that no longer exists.
    if (this.infiniteScroll && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    // A live region only announces when its text changes while it is in the DOM, and
    // _renderPage swaps the visible count out wholesale. This node survives the swap.
    if (this.infiniteScroll) {
      this.$announcer = document.createElement('p')
      this.$announcer.className = 'visually-hidden'
      this.$announcer.setAttribute('role', 'status')
      this.append(this.$announcer)
    }
    this._attachListeners()
    this._setLoadMoreListners()
  }

  disconnectedCallback() {
    this._removeListeners()
    this.loadMoreObserver?.disconnect()
  }

  get infiniteScroll() {
    return this.hasAttribute('data-infinite-scroll')
  }

  get nextPageUrl() {
    return this.getAttribute('data-paginate-next')
  }

  set nextPageUrl(value) {
    // setAttribute stringifies a null next/previous page into a truthy "null".
    if (!value) return this.removeAttribute('data-paginate-next')
    return this.setAttribute('data-paginate-next', value)
  }

  get previousPageUrl() {
    return this.getAttribute('data-paginate-previous')
  }

  set previousPageUrl(value) {
    if (!value) return this.removeAttribute('data-paginate-previous')
    return this.setAttribute('data-paginate-previous', value)
  }

  async _fetchPage(nextUrl) {
    if (nextUrl) {
      const response = await fetch(nextUrl)
      const $page = document.createElement('div')
      $page.innerHTML = await response.text()
      const collectionGrid = $page.querySelector('collection-products-grid')
      return {
        products: $page.querySelectorAll(
          'collection-products-grid #collection-products-grid > *:not([data-load-more])'
        ),
        elementToFocus: $page.querySelector('product-card a'),
        loadMoreButton: $page.querySelector(
          'collection-products-grid #collection-products-grid .collection-grid__load-more'
        ),
        paginateNext: collectionGrid.nextPageUrl,
        paginatePrevious: collectionGrid.previousPageUrl,
        filterContent: $page.querySelector('[data-filters-form]'),
        mobileFilterContent: $page.querySelector(
          '[data-filter-form-submitable]'
        ),
      }
    }

    throw new Error('No More Page to Load')
  }

  _addProductsAndCards(contents = []) {
    if (this.$loadMoreContainer) {
      this.$loadMoreContainer.before(...contents)
    } else {
      this.$grid.append(...contents)
    }
  }

  _renderPage(page, reset = false) {
    this.nextPageUrl = page.paginateNext
    this.previousPageUrl = page.paginatePrevious
    if (reset) {
      this.$grid.innerHTML = ''
      for (const item of page.products) {
        this.$grid.append(item)
      }
    } else {
      this._addProductsAndCards(page.products)
    }

    if (page.filterContent) {
      const currentOpenMenu = this.$filtersForm.querySelector(
        'collection-filter-dropdown[open]'
      )
      if (currentOpenMenu) {
        page.filterContent
          .querySelector(`#${currentOpenMenu.id}`)
          .setAttribute('open', 'true')
      }
      const visualFiltersScroll = this.$visualFilterItems?.scrollLeft
      this.$filtersForm.innerHTML = page.filterContent.innerHTML
      this.$filtersFormSubmitable.innerHTML = page.mobileFilterContent.innerHTML
      this.$visualFilterItems?.scrollTo({ left: visualFiltersScroll, behavior: 'instant' })
    }

    if (page.loadMoreButton) {
      this.$loadMoreContainer.remove()
      this.$grid.append(page.loadMoreButton)
      this._setLoadMoreListners()
    }

    if (this.$announcer) {
      this.$announcer.textContent =
        this.querySelector('.collection-grid__load-more-count')
          ?.textContent.trim() ?? ''
    }
  }

  _handleLoadMore(event) {
    event.preventDefault()
    return this._loadNextPage()
  }

  async _loadNextPage(silent = false) {
    // Outside the try: an early return here must not reach the finally and clear the
    // flag while the in-flight fetch is still running.
    if (this.loading || !this.nextPageUrl) {
      return
    }

    // A silent load shows no backdrop, so the filters stay clickable while it is in
    // flight. Stamp the request and drop the response if a filter change superseded it,
    // or its products get appended onto an already-filtered grid.
    const requestId = ++this.requestId

    try {
      this._setLoading(true, silent)
      this.$loadMore?.setAttribute('disabled', true)
      const nextPage = await this._fetchPage(this.nextPageUrl)
      if (requestId !== this.requestId) {
        return
      }
      this._renderPage(nextPage)
      // Moving focus is right for a click, but on a scroll load it yanks the viewport
      // away from what the shopper is reading. The count element announces the new total.
      if (!silent) nextPage.elementToFocus?.focus()
    } catch (error) {
      console.error(error)
      // The observer will not fire again while the container is already intersecting,
      // so a failed scroll load has to fall back to a control the shopper can use.
      this.loadMoreObserver?.disconnect()
      if (this.$loadMore) {
        this.$loadMore.removeAttribute('disabled')
        this.$loadMore.style.removeProperty('display')
      }
    } finally {
      // A superseded request must leave the flag alone - the filter fetch that took
      // over is still running behind it and owns the loading state now.
      if (requestId === this.requestId) {
        this._setLoading(false)
      }
    }
  }

  _setLoadMoreListners() {
    if (this.$loadMore) {
      this.$loadMore.removeEventListener('click', this._handleLoadMore)
    }

    this.$loadMore = this.querySelector('[data-load-more]')
    this.$loadMoreContainer = this.querySelector('.collection-grid__load-more')

    if (this.$loadMore) {
      this.$loadMore.addEventListener('click', this._handleLoadMore)

      if (this.infiniteScroll) {
        // Kept in the markup as the crawlable / no-JS path to the next page.
        // Inline style because `.button` sets `display: inline-flex`, which beats `[hidden]`.
        this.$loadMore.style.display = 'none'
      }
    }

    // _renderPage swaps the load-more container wholesale, so this re-attach point is
    // also where a stale observer gets dropped and a fresh one armed on the new node.
    this.loadMoreObserver?.disconnect()
    if (this.infiniteScroll && this.$loadMoreContainer && this.nextPageUrl) {
      this.loadMoreObserver = new IntersectionObserver(
        (entries) =>
          entries.some((entry) => entry.isIntersecting) &&
          this._loadNextPage(true),
        { rootMargin: '1200px 0px' }
      )
      this.loadMoreObserver.observe(this.$loadMoreContainer)
    }
  }

  get filterFormData() {
    return new FormData(
      this._submitedFilterForm ? this.$filtersFormSubmitable : this.$filtersForm
    )
  }

  get filterQueryString() {
    return this._clearAll
      ? new URLSearchParams({
          sort_by: this.filterFormData.get('sort_by'),
        }).toString()
      : new URLSearchParams(
          [...new Set([...this.filterFormData].map((entry) => JSON.stringify(entry)))].map((entry) =>
            JSON.parse(entry)
          )
        ).toString()
  }

  get $visualFilterItems() {
    return this.$filtersForm.querySelector('simple-slider [data-slider-items]')
  }

  _setFilterInputsChecked(name, value, checked) {
    this.$filtersForm.querySelectorAll('input[type="checkbox"]').forEach(($input) => {
      if ($input.name === name && $input.value === value) $input.checked = checked
    })
  }

  _handleFiltersPointerDown() {
    this._visualFiltersScroll = this.$visualFilterItems?.scrollLeft
  }

  _enforceSingleVisualFilter(target) {
    if (!target?.matches?.('[data-visual-filter]')) return

    if (this._visualFiltersScroll !== undefined) {
      this.$visualFilterItems?.scrollTo({ left: this._visualFiltersScroll, behavior: 'instant' })
      this._visualFiltersScroll = undefined
    }

    if (target.checked) {
      this.$filtersForm
        .querySelectorAll('[data-visual-filter]')
        .forEach(($input) => {
          if ($input !== target) this._setFilterInputsChecked($input.name, $input.value, false)
        })
    }

    this._setFilterInputsChecked(target.name, target.value, target.checked)
  }

  async _handleFiltersChange(event, queryString) {
    this._enforceSingleVisualFilter(event?.target)
    // Supersedes any scroll load already in flight; see _loadNextPage.
    this.requestId++
    try {
      if (this.$slideoutContainer) {
        this.$slideoutContainer.open = false
      }
      this._setLoading(true)
      if (!queryString) {
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}?${this.filterQueryString}`
        )
      }
      const pageData = await this._fetchPage(
        `${window.location.pathname}?${queryString ?? this.filterQueryString}`
      )
      this._renderPage(pageData, true)
    } catch (error) {
      console.log(error)
      // Nothing re-rendered, so the load-more controls are still the ones a superseded
      // scroll load left disabled, and its observer will not fire again while the
      // sentinel stays intersecting. A fresh observer reports its initial state, so
      // re-arming here is what resumes loading on the unchanged grid.
      this.$loadMore?.removeAttribute('disabled')
      this._setLoadMoreListners()
    } finally {
      this._setLoading(false)
      this._submitedFilterForm = false
      this._clearAll = false
      this.$slideoutContainer.open = false
    }
  }

  _handleFilterFormSubmit(event) {
    event.preventDefault()
    this._submitedFilterForm = true
    this._handleFiltersChange()
  }

  _handleClearAllClick(event) {
    if (event.target.hasAttribute('data-clear-all')) {
      event.preventDefault()
      this._clearAll = true
      this._handleFiltersChange()
    }
  }

  _handleLocationPopState(event) {
    this._handleFiltersChange(event, window.location.search.split('?')[1])
  }

  _attachListeners() {
    this.$filtersForm?.addEventListener('change', this._handleFiltersChange)
    this.$filtersForm?.addEventListener('pointerdown', this._handleFiltersPointerDown)
    this.$filtersFormSubmitable?.addEventListener(
      'submit',
      this._handleFilterFormSubmit
    )
    this.addEventListener('click', this._handleClearAllClick)
    window.addEventListener('popstate', this._handleLocationPopState)
  }

  _removeListeners() {
    this.$filtersForm.removeEventListener('change', this._handleFiltersChange)
    this.$filtersForm.removeEventListener('pointerdown', this._handleFiltersPointerDown)
    this.$filtersFormSubmitable.removeEventListener(
      'submit',
      this._handleFilterFormSubmit
    )
    this.removeEventListener('click', this._handleClearAllClick)
    window.removeEventListener('popstate', this._handleLocationPopState)
  }

  _setLoading(loading, silent = false) {
    this.loading = loading
    // Scroll-triggered loads are background work - no full-viewport scrim over the grid.
    this.silentLoading = loading && silent
    this._applyState()
  }

  _applyState() {
    this.$backdrop.classList[
      this.loading && !this.silentLoading ? 'add' : 'remove'
    ]('visible')
    this.classList[this.loading ? 'add' : 'remove']('loading')
  }
}

window.customElements.define('collection-products-grid', CollectionProductsGrid)
